import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconName } from 'app/shared/igntr-icons';

export interface SalesProfileFinishPayload {
  answersJson: string;
  freeText: string;
}

export interface SalesProfileProgressPayload {
  answersJson: string;
  freeText: string;
  currentIndex: number;
}

interface QuestionOption {
  /** Stable id (A–E) used only for local selection tracking. */
  id: string;
  label: string;
  /**
   * Methodology tag the answer maps to (track for Q1, focus area for Q2–Q5,
   * motivation for Q6). Kept on the model for the later scoring/plan step; the
   * page itself does not use it yet.
   */
  tag: string;
}

interface Question {
  number: number;
  title: string;
  /** e.g. "Vælg 2" / "Vælg op til 2". */
  hint: string;
  /** Optional grey helper line under the hint (Q6). */
  note?: string;
  /** Minimum answers required to advance (2 for Q1–Q5, 0 for the optional Q6). */
  min: number;
  /** Maximum answers selectable. */
  max: number;
  options: QuestionOption[];
  hasFreeText?: boolean;
  freeTextLabel?: string;
  freeTextPlaceholder?: string;
}

@Component({
  selector: 'app-sales-profile-questionnaire',
  templateUrl: './sales-profile-questionnaire.component.html',
  styleUrls: ['./sales-profile-questionnaire.component.scss'],
  standalone: false,
})
export class SalesProfileQuestionnaireComponent implements OnChanges {
  readonly letters = ['A', 'B', 'C', 'D', 'E', 'F'];

  readonly steps: { icon: IconName; title: string; sub: string }[] = [
    { icon: 'search', title: 'Find dit spor', sub: 'Jagt · Fasthold · Udvid' },
    {
      icon: 'bulb',
      title: 'Prioriter fokusområder',
      sub: 'Fx spørgeteknik & lukning',
    },
    {
      icon: 'check',
      title: 'Få en plan der passer dig',
      sub: 'Tilpasset dine mål',
    },
  ];

  // Hardcoded IGNTR sales-profile questionnaire (Danish). Structure and answer
  // tags are fixed; wording lives here as literals per the current requirement.
  readonly questions: Question[] = [
    {
      number: 1,
      title: 'Hvilke to situationer fylder mest i din salgshverdag lige nu?',
      hint: 'Vælg 2',
      min: 2,
      max: 2,
      options: [
        {
          id: 'A',
          label:
            'Jeg bruger meget tid på at booke møder med kunder, vi aldrig har solgt til før',
          tag: 'JAGT',
        },
        {
          id: 'B',
          label: 'Mit vigtigste mål lige nu er antal nye, lukkede aftaler',
          tag: 'JAGT',
        },
        {
          id: 'C',
          label:
            'Jeg bruger meget tid på at fastholde og genaktivere eksisterende kunder',
          tag: 'FASTHOLD',
        },
        {
          id: 'D',
          label: 'Mit vigtigste mål lige nu er fastholdelse og genkøb',
          tag: 'FASTHOLD',
        },
        {
          id: 'E',
          label:
            'Jeg bruger meget tid på at finde mersalgs- og krydssalgsmuligheder hos nuværende kunder',
          tag: 'UDVID',
        },
      ],
    },
    {
      number: 2,
      title: 'Før og i starten af mødet: hvor halter det oftest?',
      hint: 'Vælg 2',
      min: 2,
      max: 2,
      options: [
        {
          id: 'A',
          label:
            'Jeg møder for dårligt forberedt op — uklar dagsorden og mål med mødet',
          tag: 'Mødeforberedelse',
        },
        {
          id: 'B',
          label:
            'Jeg har svært ved hurtigt at aflæse hvilken type kunde jeg sidder overfor',
          tag: 'Læse kundetyper',
        },
        {
          id: 'C',
          label:
            'Jeg har svært ved at skabe tillid og en god tone med det samme',
          tag: 'Relationsopbygning & tillid',
        },
        {
          id: 'D',
          label:
            'Jeg mister gennemslagskraft når mødet foregår på telefon eller video',
          tag: 'Digital/telefonisk salgsdialog',
        },
        {
          id: 'E',
          label:
            'Jeg går for hurtigt videre uden at stille nok undersøgende spørgsmål',
          tag: 'Spørgeteknik',
        },
      ],
    },
    {
      number: 3,
      title: 'Under samtalen: hvor mister du kunden?',
      hint: 'Vælg 2',
      min: 2,
      max: 2,
      options: [
        {
          id: 'A',
          label:
            'Jeg lytter ikke ordentligt — jeg tænker på mit næste argument i stedet',
          tag: 'Aktiv lytning',
        },
        {
          id: 'B',
          label:
            'Jeg afdækker ikke det reelle behov, kun det kunden siger først',
          tag: 'Behovsafdækning',
        },
        {
          id: 'C',
          label: 'Mit kropssprog og min tilstedeværelse er ikke stærk nok',
          tag: 'Kropssprog & tilstedeværelse',
        },
        {
          id: 'D',
          label:
            'Jeg mangler gode historier og cases der gør budskabet konkret',
          tag: 'Storytelling & cases',
        },
        {
          id: 'E',
          label:
            'Jeg tilpasser mig ikke godt nok til den kundetype jeg sidder overfor',
          tag: 'Læse kundetyper',
        },
      ],
    },
    {
      number: 4,
      title: 'Præsentation, indvendinger og forhandling: hvor går det galt?',
      hint: 'Vælg 2',
      min: 2,
      max: 2,
      options: [
        {
          id: 'A',
          label:
            'Min præsentation mangler tydelig værdi set fra kundens perspektiv',
          tag: 'Værdiskabende præsentation',
        },
        {
          id: 'B',
          label: 'Jeg bliver bragt ud af balance af indvendinger',
          tag: 'Indvendingshåndtering',
        },
        {
          id: 'C',
          label: 'Jeg går for hurtigt på pris i stedet for at forsvare værdien',
          tag: 'Forhandlingsteknik & prisforsvar',
        },
        {
          id: 'D',
          label:
            'Jeg stiller for få spørgsmål, når kunden er i tvivl eller siger nej',
          tag: 'Spørgeteknik',
        },
        {
          id: 'E',
          label:
            'Jeg lukker aftalen for tidligt uden at have forberedt mig ordentligt på mødet',
          tag: 'Lukketeknik',
        },
      ],
    },
    {
      number: 5,
      title:
        'Lukning, opfølgning og din egen performance: hvad passer bedst?',
      hint: 'Vælg 2',
      min: 2,
      max: 2,
      options: [
        {
          id: 'A',
          label: 'Jeg tør ikke rigtig spørge om ordren og guide beslutningen',
          tag: 'Lukketeknik',
        },
        {
          id: 'B',
          label: 'Jeg mister overblikket og glemmer at følge op i tide',
          tag: 'Opfølgning & pipeline-disciplin',
        },
        {
          id: 'C',
          label:
            'Jeg mister energi og fokus, når det går trægt over længere tid',
          tag: 'Selvledelse & mindset',
        },
        {
          id: 'D',
          label:
            'Jeg lytter mindre godt, når mødet handler om detaljer og opfølgning',
          tag: 'Aktiv lytning',
        },
        {
          id: 'E',
          label: 'Jeg går ind til vigtige møder uden reelt at have forberedt mig',
          tag: 'Mødeforberedelse',
        },
      ],
    },
    {
      number: 6,
      title: 'Hvad vil du gerne opnå med dette forløb?',
      hint: 'Vælg op til 2',
      note: 'Tæller ikke med i beregningen — bruges kun til at gøre sproget i din personlige plan mere personligt.',
      min: 0,
      max: 2,
      hasFreeText: true,
      freeTextLabel: 'Valgfrit — beskriv med dine egne ord',
      freeTextPlaceholder: 'Hvad håber du at få ud af forløbet?',
      options: [
        {
          id: 'A',
          label: 'Blive mere konsistent og systematisk i min salgstilgang',
          tag: 'Struktur & konsistens',
        },
        {
          id: 'B',
          label: 'Nå mine mål og booste mine resultater hurtigere',
          tag: 'Resultater & performance',
        },
        {
          id: 'C',
          label: 'Blive mere tryg og selvsikker i de svære situationer',
          tag: 'Tryghed & selvsikkerhed',
        },
        {
          id: 'D',
          label: 'Blive dygtigere til selve håndværket og udvikle mig fagligt',
          tag: 'Faglig mestring',
        },
        {
          id: 'E',
          label: 'Performe bedre og skille mig positivt ud blandt mine kolleger',
          tag: 'Konkurrence & anerkendelse',
        },
      ],
    },
  ];

  /** Set by the host while the finish POST is in flight — disables the finish
   *  button so answers can't be submitted twice. */
  @Input() submitting = false;

  /**
   * Saved partial progress to hydrate. A JSON string in the same shape
   * buildAnswers() produces ({ answers: [{ q, options, tags }] }). Applied once
   * by the host before render.
   */
  @Input() initialAnswers: string | null = null;
  /** Question index to resume at (clamped to the valid range). */
  @Input() initialIndex = 0;

  @Output() finish = new EventEmitter<SalesProfileFinishPayload>();
  /** Emitted on every navigation so the host can persist partial progress. */
  @Output() progress = new EventEmitter<SalesProfileProgressPayload>();

  currentIndex = 0;
  /** Selected option ids per question, indexed by question position. */
  selections: string[][] = this.questions.map(() => []);
  freeText = '';
  private hydrated = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialAnswers'] || changes['initialIndex']) {
      this.hydrate();
    }
  }

  /** Restore selections / freeText / currentIndex from saved progress once. */
  private hydrate(): void {
    if (this.hydrated) {
      return;
    }
    if (this.initialAnswers) {
      try {
        const parsed = JSON.parse(this.initialAnswers) as {
          answers?: { q?: number; options?: string[] }[];
          freeText?: string;
        };
        const saved = Array.isArray(parsed?.answers) ? parsed.answers : [];
        this.questions.forEach((question, i) => {
          const match = saved.find((a) => a?.q === question.number);
          const options = Array.isArray(match?.options) ? match.options : [];
          const validIds = new Set(question.options.map((o) => o.id));
          this.selections[i] = options
            .filter((id) => validIds.has(id))
            .slice(0, question.max);
        });
        if (typeof parsed?.freeText === 'string') {
          this.freeText = parsed.freeText;
        }
      } catch {
        // Malformed saved data -> ignore and keep the empty questionnaire.
      }
    }
    const last = this.questions.length - 1;
    this.currentIndex = Math.min(Math.max(0, this.initialIndex || 0), last);
    this.hydrated = true;
  }

  get current(): Question {
    return this.questions[this.currentIndex];
  }

  get currentSelection(): string[] {
    return this.selections[this.currentIndex];
  }

  get isFirst(): boolean {
    return this.currentIndex === 0;
  }

  get isLast(): boolean {
    return this.currentIndex === this.questions.length - 1;
  }

  isSelected(optionId: string): boolean {
    return this.currentSelection.includes(optionId);
  }

  toggle(optionId: string): void {
    const selected = this.currentSelection;
    const idx = selected.indexOf(optionId);
    if (idx > -1) {
      selected.splice(idx, 1);
    } else if (selected.length < this.current.max) {
      selected.push(optionId);
    }
  }

  get canProceed(): boolean {
    return this.currentSelection.length >= this.current.min;
  }

  next(): void {
    if (!this.canProceed) {
      return;
    }
    if (this.isLast) {
      // Completion path — kept exactly as-is (host saves with isCompleted:true).
      this.finish.emit({
        answersJson: JSON.stringify(this.buildAnswers()),
        freeText: this.freeText.trim(),
      });
      return;
    }
    this.currentIndex++;
    this.emitProgress();
  }

  private emitProgress(): void {
    this.progress.emit({
      answersJson: JSON.stringify(this.buildAnswers()),
      freeText: this.freeText.trim(),
      currentIndex: this.currentIndex,
    });
  }

  private buildAnswers() {
    return {
      answers: this.questions.map((q, i) => ({
        q: q.number,
        options: this.selections[i],
        tags: this.selections[i].map(
          (id) => q.options.find((o) => o.id === id)?.tag,
        ),
      })),
    };
  }

  back(): void {
    if (!this.isFirst) {
      this.currentIndex--;
      this.emitProgress();
    }
  }

  goTo(index: number): void {
    if (index >= 0 && index < this.questions.length) {
      this.currentIndex = index;
      this.emitProgress();
    }
  }
}
